import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { FaCoins } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthProvider";
import { useUserConfig } from "../../../context/UserConfigProvider";
import {
  CREATE_TIPPER,
  GET_TIPPERS_WITH_CHIPS,
  UPDATE_TIPPER_CHIPS,
} from "../../../queries/mutation/broadcaster/chipsQueries";
import LoadingPage from "../../LoadingPage";

interface TipperWithChips {
  id: string;
  username: string;
  chips: number;
  currentTips: number;
  inFanclub: boolean;
  isFollower: boolean;
  isMod: boolean;
}

const ChipsLeaderboard = () => {
  const { permissions } = useUserConfig();
  const { user } = useAuth();
  const [searchUsername, setSearchUsername] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [minCurrentTips, setMinCurrentTips] = useState<string>("");
  const [debouncedMinTips, setDebouncedMinTips] = useState<string>("");
  const [selectedTipper, setSelectedTipper] = useState<TipperWithChips | null>(
    null,
  );
  const [chipAmount, setChipAmount] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newChipsAmount, setNewChipsAmount] = useState<string>("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchUsername);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchUsername]);

  // Debounce min tips input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMinTips(minCurrentTips);
    }, 500);

    return () => clearTimeout(timer);
  }, [minCurrentTips]);

  const { loading, error, data, refetch } = useQuery(GET_TIPPERS_WITH_CHIPS, {
    variables: {
      username: debouncedSearch || undefined,
      minCurrentTips: debouncedMinTips ? parseInt(debouncedMinTips) : undefined,
    },
    fetchPolicy: "network-only",
    pollInterval: 10000, // Refresh every 10 seconds
  });

  const [updateTipperChips, { loading: updateLoading }] = useMutation(
    UPDATE_TIPPER_CHIPS,
    {
      onCompleted: (data) => {
        toast.success("Chips updated successfully!");
        refetch();
        // Update selectedTipper with new data to reflect changes in modal
        if (selectedTipper && data?.updateTipperChips) {
          setSelectedTipper(data.updateTipperChips);
        }
        setChipAmount("");
      },
      onError: (error) => {
        toast.error(`Failed to update chips: ${error.message}`);
      },
    },
  );

  const [createTipper, { loading: createLoading }] = useMutation(
    CREATE_TIPPER,
    {
      onCompleted: () => {
        toast.success("Tipper created successfully!");
        refetch();
        setShowCreateModal(false);
        setNewUsername("");
        setNewChipsAmount("");
      },
      onError: (error) => {
        toast.error(`Failed to create tipper: ${error.message}`);
      },
    },
  );

  const handleUpdateChips = async (amount: number) => {
    if (!selectedTipper) return;

    if (amount === 0) {
      toast.warning("Amount cannot be zero");
      return;
    }

    await updateTipperChips({
      variables: {
        input: {
          username: selectedTipper.username,
          amount,
        },
      },
    });
  };

  const handleQuickUpdate = async (amount: number) => {
    if (!selectedTipper) return;

    await updateTipperChips({
      variables: {
        input: {
          username: selectedTipper.username,
          amount,
        },
      },
    });
  };

  const handleCopyToClipboard = () => {
    // Filter tippers with chips > 0 and format as "username: chips"
    const tippersWithChips = sortedTippers
      .filter((tipper) => (tipper.chips || 0) > 0)
      .map((tipper) => `${tipper.username}: ${tipper.chips}`)
      .join("\n");

    if (tippersWithChips) {
      navigator.clipboard
        .writeText(tippersWithChips)
        .then(() => {
          toast.success("Copied to clipboard!");
        })
        .catch(() => {
          toast.error("Failed to copy to clipboard");
        });
    } else {
      toast.warning("No tippers with chips to copy");
    }
  };

  const handleGenerateRaffleList = () => {
    // Filter tippers with chips > 0 and format as "username, raffle_count, ..."
    const raffleEntries = sortedTippers
      .filter((tipper) => (tipper.chips || 0) > 0)
      .flatMap((tipper) => [
        tipper.username,
        Math.ceil((tipper.chips || 0) / 200).toString(),
      ]);

    if (raffleEntries.length > 0) {
      const raffleList = raffleEntries.join(", ");
      navigator.clipboard
        .writeText(raffleList)
        .then(() => {
          toast.success("Raffle list copied to clipboard!");
        })
        .catch(() => {
          toast.error("Failed to copy raffle list");
        });
    } else {
      toast.warning("No tippers with chips to generate raffle list");
    }
  };

  const handleCreateTipper = async () => {
    if (!newUsername.trim()) {
      toast.warning("Please enter a username");
      return;
    }

    const chips = newChipsAmount.trim() ? parseInt(newChipsAmount) : undefined;

    await createTipper({
      variables: {
        input: {
          username: newUsername.trim(),
          ...(chips !== undefined && { chips }),
        },
      },
    });
  };

  // Check if user is a broadcaster or mod
  if (!user?.isBroadcaster && !permissions.isMod) {
    return (
      <div className="h-full flex items-center justify-center bg-base-200">
        <div className="text-center">
          <div className="text-error text-2xl font-bold mb-4">
            Access Denied
          </div>
          <div className="text-gray-400">
            Only broadcasters and mods can view the chips leaderboard
          </div>
        </div>
      </div>
    );
  }

  const allTippers: TipperWithChips[] =
    data?.getTippersWithChips?.tippers || [];
  const totalChips = data?.getTippersWithChips?.totalChips || 0;

  // Sort tippers by chips descending (already sorted by API, but keeping for consistency)
  const sortedTippers = [...allTippers].sort((a, b) => {
    return (b.chips || 0) - (a.chips || 0);
  });

  return (
    <div className="bg-base-200 h-full flex flex-col p-0 sm:p-4 gap-4">
      <div className="card bg-base-100 h-full flex flex-col">
        <div className="card-body flex flex-col h-full !p-2 sm:!p-4">
          <h2 className="card-title flex items-center gap-2">
            <FaCoins size={24} className="text-yellow-500" />
            Chips Manager
            <div className="badge badge-lg badge-warning ml-auto">
              Total: {totalChips.toLocaleString()}
            </div>
          </h2>

          <div className="divider m-0" />

          {/* Search and Filter Controls */}
          <div className="flex flex-col gap-3 mb-4 flex-shrink-0">
            {/* Search Bar */}
            <div className="flex flex-wrap gap-2">
              <div className="flex-1 min-w-[200px] input-group">
                <input
                  type="text"
                  placeholder="Search username..."
                  className="input input-bordered w-full"
                  value={searchUsername}
                  onChange={(e) => setSearchUsername(e.target.value)}
                />
              </div>
              <div className="w-full sm:w-48">
                <input
                  type="number"
                  placeholder="Min tokens"
                  className="input input-bordered w-full"
                  value={minCurrentTips}
                  onChange={(e) => setMinCurrentTips(e.target.value)}
                  min="0"
                />
              </div>
              <button
                className="btn btn-success flex-1 sm:flex-initial"
                onClick={() => setShowCreateModal(true)}
              >
                Create Tipper
              </button>
              <button
                className="btn btn-secondary flex-1 sm:flex-initial"
                onClick={handleCopyToClipboard}
                disabled={sortedTippers.length === 0}
              >
                Copy Chip List
              </button>
              <button
                className="btn btn-accent flex-1 sm:flex-initial"
                onClick={handleGenerateRaffleList}
                disabled={sortedTippers.length === 0}
              >
                Generate Raffle List
              </button>
              <button
                className="btn btn-primary flex-1 sm:flex-initial"
                onClick={() => refetch()}
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Tippers Table */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingPage />
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-error text-xl font-bold mb-4">
                  Error loading tippers
                </div>
                <div className="text-gray-400">
                  {error?.message || "Unknown error"}
                </div>
              </div>
            </div>
          ) : sortedTippers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No tippers found
            </div>
          ) : (
            <div className="overflow-auto flex-1 min-h-0">
              <table className="table table-compact w-full">
                <thead>
                  <tr className="border-b border-gray-600">
                    <th className="text-left">Username</th>
                    <th className="text-right">Chips</th>
                    <th className="text-right">Raffle Tickets</th>
                    <th className="text-right">Current Tips</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTippers.map((tipper, index) => (
                    <tr
                      key={tipper.id}
                      className="border-b border-gray-700 hover:bg-base-300 transition cursor-pointer"
                      onClick={() => setSelectedTipper(tipper)}
                    >
                      <td className="font-semibold">{tipper.username}</td>
                      <td className="text-right">
                        <span className="badge badge-lg badge-warning">
                          {(tipper.chips || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="text-right">
                        <span className="badge badge-lg badge-info">
                          {Math.ceil((tipper.chips || 0) / 200)}
                        </span>
                      </td>
                      <td className="text-right">
                        {(tipper.currentTips || 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Update Chips Modal */}
      {selectedTipper && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              Update Chips for{" "}
              <span className="text-primary">{selectedTipper.username}</span>
            </h3>

            <div className="flex flex-col gap-4">
              <div className="stats shadow">
                <div className="stat">
                  <div className="stat-title">Current Chips</div>
                  <div className="stat-value text-warning">
                    {(selectedTipper.chips || 0).toLocaleString()}
                  </div>
                  <div className="stat-desc">
                    {Math.floor((selectedTipper.chips || 0) / 100)} raffle
                    tickets
                  </div>
                </div>
              </div>

              <div className="divider">Quick Actions</div>

              <div className="grid grid-cols-3 gap-2">
                <button
                  className="btn btn-success"
                  onClick={() => handleQuickUpdate(50)}
                  disabled={updateLoading}
                >
                  +50
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => handleQuickUpdate(100)}
                  disabled={updateLoading}
                >
                  +100
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => handleQuickUpdate(500)}
                  disabled={updateLoading}
                >
                  +500
                </button>
                <button
                  className="btn btn-error"
                  onClick={() => handleQuickUpdate(-50)}
                  disabled={updateLoading}
                >
                  -50
                </button>
                <button
                  className="btn btn-error"
                  onClick={() => handleQuickUpdate(-100)}
                  disabled={updateLoading}
                >
                  -100
                </button>
                <button
                  className="btn btn-error"
                  onClick={() => handleQuickUpdate(-500)}
                  disabled={updateLoading}
                >
                  -500
                </button>
              </div>

              <div className="divider">Custom Amount</div>

              <div className="flex gap-2">
                <input
                  type="number"
                  className="input input-bordered flex-1"
                  placeholder="Enter amount (+ or -)"
                  value={chipAmount}
                  onChange={(e) => setChipAmount(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && chipAmount) {
                      handleUpdateChips(parseInt(chipAmount));
                    }
                  }}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => handleUpdateChips(parseInt(chipAmount))}
                  disabled={updateLoading || !chipAmount}
                >
                  Apply
                </button>
              </div>
            </div>

            <div className="modal-action">
              <button
                className="btn"
                onClick={() => {
                  setSelectedTipper(null);
                  setChipAmount("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Tipper Modal */}
      {showCreateModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Create New Tipper</h3>

            <div className="flex flex-col gap-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Username</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered w-full"
                  placeholder="Enter username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && newUsername.trim()) {
                      handleCreateTipper();
                    }
                  }}
                  autoFocus
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Initial Chips (Optional)</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  placeholder="0"
                  value={newChipsAmount}
                  onChange={(e) => setNewChipsAmount(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && newUsername.trim()) {
                      handleCreateTipper();
                    }
                  }}
                  min="0"
                />
              </div>

              <div className="alert alert-info">
                <div className="flex-1">
                  <div className="text-sm text-center">
                    A new tipper will be created with the specified chips
                    (default: 0) and 0 tips.
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-action">
              <button
                className="btn btn-success"
                onClick={handleCreateTipper}
                disabled={createLoading || !newUsername.trim()}
              >
                {createLoading ? "Creating..." : "Create"}
              </button>
              <button
                className="btn"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewUsername("");
                  setNewChipsAmount("");
                }}
                disabled={createLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChipsLeaderboard;
